/**
 * Auswertungslisten (Berichte).
 *
 * Bericht und Zeitraum wählen, Tabelle ansehen, als CSV (Excel) oder PDF
 * (Ablage/Druck) ausgeben. Die Spalten kommen vom Server mit - eine neue
 * Auswertung braucht hier keine Änderung.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  BUNDESLAENDER,
  DISPATCH_CHANNELS,
  DOCUMENT_TYPES,
  formatAmount,
  formatPrice,
  formatQuantity,
  INTRASTAT_TRANSACTION_TYPES,
  INTRASTAT_TRANSPORT_MODES,
  type DocumentType,
} from '@erp/shared';

import { api, ApiError, apiUrl, withQuery } from '../api/client';
import type { ListResponse, Partner } from '../api/types';
import { ItemPicker } from '../components/ItemPicker';
import { PageHeader } from '../components/Layout';
import { SearchSelect } from '../components/SearchSelect';
import { useToast } from '../components/toast';
import { Alert, Checkbox, Dialog, EmptyState, Field, LoadingBlock, Select, TextArea, TextInput } from '../components/ui';

type ReportKey =
  | 'employees'
  | 'customers'
  | 'items'
  | 'open-items'
  | 'item-history'
  | 'intrastat'
  | 'dispatch-journal'
  | 'price-list';

type ReportColumn = { key: string; label: string; kind: 'text' | 'count' | 'amount' | 'price' | 'quantity' };

type ReportResult = {
  title: string;
  subtitle: string;
  columns: ReportColumn[];
  rows: Record<string, string | number | null>[];
  totals: Record<string, string | number | null>;
  warnings?: string[];
};

const REPORTS: { key: ReportKey; label: string; hasPeriod: boolean }[] = [
  { key: 'employees', label: 'Belege je Mitarbeiter', hasPeriod: true },
  { key: 'customers', label: 'Umsatz je Kunde', hasPeriod: true },
  { key: 'items', label: 'Umsatz je Artikel / Warengruppe', hasPeriod: true },
  { key: 'open-items', label: 'Offene Posten nach Fälligkeit', hasPeriod: false },
  { key: 'item-history', label: 'Artikelhistorie', hasPeriod: true },
  { key: 'intrastat', label: 'Intrastat-Meldung', hasPeriod: false },
  { key: 'dispatch-journal', label: 'Ausgangsjournal (Versandwege)', hasPeriod: true },
  { key: 'price-list', label: 'Preisliste (Warengruppen, wahlweise je Kunde)', hasPeriod: false },
];

/** Vormonat als JJJJ-MM - der Bezugsmonat, der meist gemeldet wird. */
function previousMonth(): string {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function firstOfMonth(shiftMonths = 0): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + shiftMonths, 1).toISOString().slice(0, 10);
}

function lastOfMonth(shiftMonths = 0): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + shiftMonths + 1, 0).toISOString().slice(0, 10);
}

function cell(column: ReportColumn, value: string | number | null): string {
  if (column.kind === 'price') return value === null ? '' : `${formatPrice(Number(value))} €`;
  if (value === null || value === undefined) return '';
  if (column.kind === 'amount') return `${formatAmount(Number(value))} €`;
  if (column.kind === 'quantity') return formatQuantity(Number(value));
  return String(value);
}

export function ReportsPage() {
  // Aus Artikel- und Kundenakte kommt man mit Vorbelegung her:
  // /auswertungen/berichte?report=item-history&itemId=…&partnerId=…
  const [searchParams] = useSearchParams();
  const initialReport = (searchParams.get('report') as ReportKey | null) ?? 'employees';
  const toast = useToast();

  const [report, setReport] = useState<ReportKey>(
    REPORTS.some((entry) => entry.key === initialReport) ? initialReport : 'employees',
  );
  const [from, setFrom] = useState(initialReport === 'item-history' ? firstOfMonth(-11) : firstOfMonth());
  const [to, setTo] = useState(lastOfMonth());
  const [itemId, setItemId] = useState(searchParams.get('itemId') ?? '');
  const [itemLabel, setItemLabel] = useState(searchParams.get('itemLabel') ?? '');
  const [partnerId, setPartnerId] = useState(searchParams.get('partnerId') ?? '');
  const [side, setSide] = useState<'sales' | 'purchase' | 'all'>('all');
  const [mailOpen, setMailOpen] = useState(false);
  const [attribution, setAttribution] = useState<'erfasser' | 'aussendienst'>('erfasser');
  const [docType, setDocType] = useState<DocumentType>('invoice');
  const [groupBy, setGroupBy] = useState<'item' | 'category'>('item');
  // Intrastat: Bezugsmonat und die Schluessel der Meldung.
  const [month, setMonth] = useState(previousMonth);
  const [direction, setDirection] = useState<'dispatch' | 'arrival'>('dispatch');
  const [state, setState] = useState('05');
  const [transport, setTransport] = useState('3');
  const [nature, setNature] = useState('11');
  // Vorgaben der Schluessel aus den Firmendaten - einmal vorbelegen, danach frei aenderbar.
  const { data: intrastatDefaults } = useQuery({
    queryKey: ['intrastat-defaults'],
    queryFn: () => api.get<{ state: string; transport: string; nature: string }>('/api/reports/intrastat/defaults'),
    enabled: report === 'intrastat',
  });
  useEffect(() => {
    if (!intrastatDefaults) return;
    setState(intrastatDefaults.state);
    setTransport(intrastatDefaults.transport);
    setNature(intrastatDefaults.nature);
  }, [intrastatDefaults]);
  // Preisliste: gewaehlte Warengruppen (leer = alle) und wahlweise ein Kunde.
  const [priceCategoryIds, setPriceCategoryIds] = useState<string[]>([]);
  const [pricePartnerId, setPricePartnerId] = useState('');
  const { data: categoryList } = useQuery({
    queryKey: ['categories', 'reports'],
    queryFn: () =>
      api.get<{ categories: { id: string; number: string | null; name: string; parentId: string | null; itemCount: number }[] }>(
        '/api/categories?active=true',
      ),
    enabled: report === 'price-list',
  });
  // Ausgangsjournal: Belegart und Weg.
  const [journalType, setJournalType] = useState<DocumentType | 'all'>('invoice');
  const [journalChannel, setJournalChannel] = useState('all');

  const meta = REPORTS.find((entry) => entry.key === report)!;

  const params: Record<string, string> = {
    ...(meta.hasPeriod ? { from, to } : {}),
    ...(report === 'employees' ? { attribution, docType } : {}),
    ...(report === 'items' ? { groupBy } : {}),
    ...(report === 'intrastat' ? { month, direction, state, transport, nature } : {}),
    ...(report === 'dispatch-journal' ? { docType: journalType, channel: journalChannel } : {}),
    ...(report === 'price-list'
      ? { categoryIds: priceCategoryIds.join(','), ...(pricePartnerId ? { partnerId: pricePartnerId } : {}) }
      : {}),
    ...(report === 'item-history'
      ? { side, ...(itemId ? { itemId } : {}), ...(partnerId ? { partnerId } : {}) }
      : {}),
  };
  // Die Historie braucht einen Artikel oder einen Partner - aus der
  // Kundenakte kommt man mit dem Partner allein.
  const ready = report !== 'item-history' || Boolean(itemId) || Boolean(partnerId);

  const { data, isLoading } = useQuery({
    queryKey: ['report', report, params],
    queryFn: () => api.get<ReportResult>(withQuery(`/api/reports/${report}`, params)),
    enabled: ready,
  });

  const { data: partnersList } = useQuery({
    queryKey: ['partners', 'all', 'reports'],
    queryFn: () => api.get<ListResponse<Partner>>(withQuery('/api/partners', { limit: 500 })),
    enabled: report === 'item-history' || report === 'price-list',
  });

  function exportAs(format: 'csv' | 'pdf') {
    window.open(apiUrl(withQuery(`/api/reports/${report}`, { ...params, format })), '_blank');
  }

  return (
    <>
      <PageHeader
        title="Berichte"
        actions={
          <div className="row" style={{ gap: 6 }}>
            <button type="button" className="btn btn-sm" onClick={() => exportAs('csv')}>
              {report === 'intrastat' ? 'Meldedatei (CSV für IDEV)' : 'CSV (Excel)'}
            </button>
            <button type="button" className="btn btn-sm" onClick={() => exportAs('pdf')}>
              PDF / Drucken
            </button>
            <button
              type="button"
              className="btn btn-sm"
              disabled={!ready || !data}
              onClick={() => setMailOpen(true)}
            >
              Per E-Mail senden
            </button>
          </div>
        }
      />

      {mailOpen && data ? (
        <ReportMailDialog
          report={report}
          params={params}
          title={data.title}
          onClose={() => setMailOpen(false)}
        />
      ) : null}

      <div className="card" style={{ marginBottom: 14, padding: 12 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label="Bericht">
            <Select
              value={report}
              onChange={(value) => setReport(value)}
              options={REPORTS.map((entry) => ({ value: entry.key, label: entry.label }))}
            />
          </Field>

          {meta.hasPeriod ? (
            <>
              <Field label="Von">
                <TextInput type="date" value={from} onChange={setFrom} />
              </Field>
              <Field label="Bis">
                <TextInput type="date" value={to} onChange={setTo} />
              </Field>
              <div className="row" style={{ gap: 4, paddingBottom: 8 }}>
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => {
                    setFrom(firstOfMonth());
                    setTo(lastOfMonth());
                  }}
                >
                  Dieser Monat
                </button>
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => {
                    setFrom(firstOfMonth(-1));
                    setTo(lastOfMonth(-1));
                  }}
                >
                  Letzter Monat
                </button>
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => {
                    setFrom(`${new Date().getFullYear()}-01-01`);
                    setTo(`${new Date().getFullYear()}-12-31`);
                  }}
                >
                  Dieses Jahr
                </button>
              </div>
            </>
          ) : null}

          {report === 'item-history' ? (
            <>
              <Field label="Artikel" hint="leer = alle Artikel des gewählten Partners">
                <div style={{ minWidth: 320 }}>
                  <ItemPicker
                    value={itemLabel}
                    onSelect={(item) => {
                      setItemId(item.id);
                      setItemLabel(`${item.number} · ${item.name}`);
                    }}
                  />
                </div>
              </Field>
              <Field label="Kunde / Lieferant" hint="leer = alle">
                <div style={{ minWidth: 260 }}>
                  <SearchSelect
                    value={partnerId}
                    onChange={setPartnerId}
                    options={(partnersList?.items ?? []).map((p) => ({ value: p.id, label: `${p.number} · ${p.name}` }))}
                    placeholder="Name oder Nummer tippen …"
                    emptyLabel="Alle"
                  />
                </div>
              </Field>
              <Field label="Seite">
                <Select
                  value={side}
                  onChange={setSide}
                  options={[
                    { value: 'all' as const, label: 'Verkauf und Einkauf' },
                    { value: 'sales' as const, label: 'Nur Verkauf' },
                    { value: 'purchase' as const, label: 'Nur Einkauf' },
                  ]}
                />
              </Field>
            </>
          ) : null}

          {report === 'employees' ? (
            <>
              <Field label="Zählung nach">
                <Select
                  value={attribution}
                  onChange={(value) => setAttribution(value)}
                  options={[
                    { value: 'erfasser' as const, label: 'Erfasser des Belegs' },
                    { value: 'aussendienst' as const, label: 'Zuständiger Außendienst' },
                  ]}
                />
              </Field>
              <Field label="Belegart">
                <Select
                  value={docType}
                  onChange={(value) => setDocType(value)}
                  options={Object.values(DOCUMENT_TYPES).map((entry) => ({
                    value: entry.type,
                    label: entry.labelPlural,
                  }))}
                />
              </Field>
            </>
          ) : null}

          {report === 'price-list' ? (
            <>
              <Field label="Kunde" hint="leer = Listenpreise; mit Kunde: Rahmenvertragspreise und Kundenrabatt">
                <div style={{ minWidth: 280 }}>
                  <SearchSelect
                    value={pricePartnerId}
                    onChange={setPricePartnerId}
                    options={(partnersList?.items ?? [])
                      .filter((p) => p.isCustomer)
                      .map((p) => ({ value: p.id, label: `${p.number} · ${p.name}` }))}
                    placeholder="Name oder Nummer tippen …"
                    emptyLabel="Listenpreise"
                  />
                </div>
              </Field>
              <Field label="Warengruppen" hint="keine Auswahl = alle; Untergruppen sind enthalten">
                <div className="row" style={{ gap: 12, flexWrap: 'wrap', paddingTop: 6 }}>
                  {(categoryList?.categories ?? []).map((category) => (
                    <Checkbox
                      key={category.id}
                      id={`price-cat-${category.id}`}
                      checked={priceCategoryIds.includes(category.id)}
                      onChange={(checked) =>
                        setPriceCategoryIds((current) =>
                          checked ? [...current, category.id] : current.filter((id) => id !== category.id),
                        )
                      }
                      label={`${category.number ? `${category.number} ` : ''}${category.name} (${category.itemCount})`}
                    />
                  ))}
                  {categoryList && categoryList.categories.length === 0 ? (
                    <span className="faint">Keine Warengruppen angelegt.</span>
                  ) : null}
                </div>
              </Field>
            </>
          ) : null}

          {report === 'items' ? (
            <Field label="Gruppierung">
              <Select
                value={groupBy}
                onChange={(value) => setGroupBy(value)}
                options={[
                  { value: 'item' as const, label: 'Je Artikel' },
                  { value: 'category' as const, label: 'Je Warengruppe' },
                ]}
              />
            </Field>
          ) : null}

          {report === 'intrastat' ? (
            <>
              <Field label="Bezugsmonat">
                <TextInput type="month" value={month} onChange={setMonth} />
              </Field>
              <Field label="Verkehrsrichtung">
                <Select
                  value={direction}
                  onChange={(value) => setDirection(value)}
                  options={[
                    { value: 'dispatch' as const, label: 'Versendung (Verkauf in die EU)' },
                    { value: 'arrival' as const, label: 'Eingang (Einkauf aus der EU)' },
                  ]}
                />
              </Field>
              <Field label="Bundesland">
                <Select
                  value={state}
                  onChange={(value) => setState(value)}
                  options={BUNDESLAENDER.map((entry) => ({ value: entry.code, label: `${entry.code} ${entry.label}` }))}
                />
              </Field>
              <Field label="Verkehrszweig">
                <Select
                  value={transport}
                  onChange={(value) => setTransport(value)}
                  options={INTRASTAT_TRANSPORT_MODES.map((entry) => ({ value: entry.code, label: `${entry.code} ${entry.label}` }))}
                />
              </Field>
              <Field label="Art des Geschäfts">
                <Select
                  value={nature}
                  onChange={(value) => setNature(value)}
                  options={INTRASTAT_TRANSACTION_TYPES.map((entry) => ({ value: entry.code, label: `${entry.code} ${entry.label}` }))}
                />
              </Field>
            </>
          ) : null}

          {report === 'dispatch-journal' ? (
            <>
              <Field label="Belegart">
                <Select
                  value={journalType}
                  onChange={(value) => setJournalType(value)}
                  options={[
                    { value: 'all' as const, label: 'Alle Belegarten' },
                    ...Object.values(DOCUMENT_TYPES).map((entry) => ({ value: entry.type, label: entry.labelPlural })),
                  ]}
                />
              </Field>
              <Field label="Weg">
                <Select
                  value={journalChannel}
                  onChange={setJournalChannel}
                  options={[{ value: 'all', label: 'Alle Wege' }, ...DISPATCH_CHANNELS.map((entry) => ({ value: entry.code, label: entry.label }))]}
                />
              </Field>
            </>
          ) : null}
        </div>
      </div>

      <div className="card">
        {!ready ? (
          <EmptyState
            title="Artikel oder Partner wählen"
            hint="Die Historie zeigt alle festgeschriebenen Belege mit diesem Artikel - oder alle Artikel eines Kunden bzw. Lieferanten."
          />
        ) : isLoading || !data ? (
          <LoadingBlock />
        ) : (
          <>
            <div className="card-header">
              <div>
                <h2>{data.title}</h2>
                <div className="faint" style={{ fontSize: 12.5 }}>{data.subtitle}</div>
              </div>
              <div className="faint" style={{ fontSize: 12.5 }}>
                {data.rows.length} {data.rows.length === 1 ? 'Zeile' : 'Zeilen'}
              </div>
            </div>
            {data.warnings && data.warnings.length > 0 ? (
              <div style={{ padding: '0 14px' }}>
                <Alert kind="warning">
                  <strong>Fehlende Angaben ({data.warnings.length})</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {data.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </Alert>
              </div>
            ) : null}
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    {data.columns.map((column) => (
                      <th key={column.key} className={column.kind === 'text' ? '' : 'numeric'}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr>
                      <td colSpan={data.columns.length} className="faint">
                        Keine Daten im gewählten Zeitraum.
                      </td>
                    </tr>
                  ) : (
                    data.rows.map((row, index) => (
                      <tr key={index}>
                        {data.columns.map((column) => (
                          <td
                            key={column.key}
                            className={column.kind === 'text' ? '' : 'numeric mono'}
                          >
                            {cell(column, row[column.key] ?? null)}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
                {data.rows.length > 0 ? (
                  <tfoot>
                    <tr style={{ fontWeight: 600 }}>
                      {data.columns.map((column) => (
                        <td
                          key={column.key}
                          className={column.kind === 'text' ? '' : 'numeric mono'}
                        >
                          {cell(column, data.totals[column.key] ?? null)}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/** Bericht als PDF per E-Mail - der Server rechnet ihn mit denselben Parametern neu. */
function ReportMailDialog({
  report,
  params,
  title,
  onClose,
}: {
  report: ReportKey;
  params: Record<string, string>;
  title: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState(title);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => api.post<{ sent: boolean; to: string }>('/api/reports/mail', { report, params, to, subject, text }),
    onSuccess: (result) => {
      toast.success(`Der Bericht wurde an ${result.to} gesendet.`);
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Der Versand ist fehlgeschlagen.'),
  });

  return (
    <Dialog
      title="Bericht per E-Mail senden"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={send.isPending}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={send.isPending || !to.trim() || !subject.trim()}
            onClick={() => {
              setError(null);
              send.mutate();
            }}
          >
            {send.isPending ? 'Wird gesendet …' : 'Senden'}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <Field label="Empfänger" required>
        <TextInput type="email" value={to} onChange={setTo} placeholder="name@firma.de" autoFocus />
      </Field>
      <Field label="Betreff" required>
        <TextInput value={subject} onChange={setSubject} maxLength={200} />
      </Field>
      <Field label="Text" hint="Leer = kurzer Standardtext mit Berichtstitel und Zeitraum">
        <TextArea value={text} onChange={setText} rows={5} />
      </Field>
      <p className="faint" style={{ marginTop: 0 }}>Der Bericht geht als PDF-Anhang mit, so wie er hier zu sehen ist.</p>
    </Dialog>
  );
}
